/**
 * AspectRatioMode V2
 *
 * Seitenverhältnis ändern mit:
 * - Upload oder Workspace-Auswahl (einzelnes Bild)
 * - Verschiedene Ziel-Verhältnisse
 * - AI-gesteuerte Crop-Modi
 * - Optionale Hintergrund-Erweiterung
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Maximize2, Check, Image as ImageIcon, Grid3X3, Ruler } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, EmptyState, Loading } from '@/components/v2/ui';
import { InputSourceToggle, UploadArea, ResultPreview, type InputSource } from '@/components/v2/tools/shared';
import { ImageGallery } from '@/components/v2/tools/gallery';
import { useWorkspaceImages } from '@/hooks/useWorkspaceImages';
import {
  changeAspectRatioUpload,
  type AspectRatioParams,
  type UploadOperationResult,
} from '@/lib/api/imageOperations';
import { cn } from '@/lib/utils';

type ProcessingState = 'idle' | 'processing' | 'done';
type TargetRatio = AspectRatioParams['target_ratio'];
type CropMode = NonNullable<AspectRatioParams['crop_mode']>;

const RATIO_KEYS: { value: TargetRatio; labelKey: string; icon: string }[] = [
  { value: '1:1', labelKey: 'editor.ratioSquare', icon: '■' },
  { value: '3:2', labelKey: 'editor.ratioLandscape', icon: '▬' },
  { value: '4:3', labelKey: 'editor.ratioStandard', icon: '▬' },
  { value: '16:9', labelKey: 'editor.ratioWidescreen', icon: '▬' },
  { value: '2:3', labelKey: 'editor.ratioPortrait', icon: '▮' },
  { value: '9:16', labelKey: 'editor.ratioStory', icon: '▮' },
];

const CROP_MODE_KEYS: { value: CropMode; labelKey: string; descKey: string }[] = [
  { value: 'smart', labelKey: 'editor.cropSmart', descKey: 'editor.cropSmartDesc' },
  { value: 'product', labelKey: 'editor.cropProduct', descKey: 'editor.cropProductDesc' },
  { value: 'person', labelKey: 'editor.cropPerson', descKey: 'editor.cropPersonDesc' },
  { value: 'center', labelKey: 'editor.cropCenter', descKey: 'editor.cropCenterDesc' },
];

export function AspectRatioMode() {
  const { t } = useTranslation();
  // Input State
  const [inputSource, setInputSource] = useState<InputSource>('upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Settings State
  const [targetRatio, setTargetRatio] = useState<TargetRatio>('1:1');
  const [cropMode, setCropMode] = useState<CropMode>('smart');
  const [extendBackground, setExtendBackground] = useState(true);

  // Custom Dimensions State
  const [customMode, setCustomMode] = useState(false);
  const [customWidth, setCustomWidth] = useState<number | ''>(1024);
  const [customHeight, setCustomHeight] = useState<number | ''>(1024);

  // Processing State
  const [state, setState] = useState<ProcessingState>('idle');
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<UploadOperationResult | null>(null);

  // Workspace Images Query
  const { images, pagination, isLoading, isFetching } = useWorkspaceImages({
    page: currentPage,
    perPage: 12,
    enabled: inputSource === 'workspace',
  });

  // Handlers
  const handleSourceChange = (source: InputSource) => {
    setInputSource(source);
    setUploadedFile(null);
    setSelectedAssetId(null);
    setResult(null);
    setState('idle');
  };

  const handleFilesChange = (files: File[]) => {
    setUploadedFile(files[0] || null);
    setResult(null);
    setState('idle');
  };

  const handleSelectionChange = useCallback((ids: Set<string>) => {
    const firstId = Array.from(ids)[0] || null;
    setSelectedAssetId(firstId);
    setResult(null);
    setState('idle');
  }, []);

  const handleProcess = async () => {
    setState('processing');
    setProgress(0);
    setResult(null);

    try {
      let fileToProcess: File | null = null;

      if (inputSource === 'upload') {
        fileToProcess = uploadedFile;
      } else if (selectedAssetId) {
        // Download workspace image as file
        const response = await fetch(`/image/${selectedAssetId}`);
        const blob = await response.blob();
        fileToProcess = new File([blob], `image_${selectedAssetId}.png`, { type: blob.type });
      }

      if (!fileToProcess) {
        throw new Error(t('editor.noImageSelected'));
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + Math.random() * 15, 90));
      }, 400);

      const response = await changeAspectRatioUpload(fileToProcess, {
        target_ratio: targetRatio,
        crop_mode: cropMode,
        extend_background: extendBackground,
        ...(customMode && typeof customWidth === 'number' && { target_width: customWidth }),
        ...(customMode && typeof customHeight === 'number' && { target_height: customHeight }),
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (response.success) {
        setResult(response);
        setState('done');
        toast.success(t('editor.aspectRatioChanged'));
      } else {
        throw new Error(response.error || t('errors.generic'));
      }
    } catch (error) {
      setState('idle');
      setProgress(0);
      toast.error(error instanceof Error ? error.message : t('common.processing'));
    }
  };

  const handleReset = () => {
    setState('idle');
    setProgress(0);
    setResult(null);
    setUploadedFile(null);
    setSelectedAssetId(null);
  };

  const hasInput = inputSource === 'upload' ? !!uploadedFile : !!selectedAssetId;
  const isIdle = state === 'idle';
  const isProcessing = state === 'processing';
  const canProcess = hasInput && isIdle;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
          <Maximize2 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">
            {t('editor.aspectRatioTitle')}
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t('editor.aspectRatioDescription')}
          </p>
        </div>
      </div>

      {state === 'done' && result ? (
        // Result View
        <ResultPreview
          assetId={result.image_id || ''}
          imageUrl={result.image_url}
          message={t('editor.aspectRatioChanged')}
          originalDimensions={result.original_dimensions}
          newDimensions={result.new_dimensions}
          processingTimeMs={result.processing_time_ms}
          onReset={handleReset}
        />
      ) : (
        // Input View
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Section */}
          <div className="lg:col-span-2 space-y-4">
            <InputSourceToggle value={inputSource} onChange={handleSourceChange} />

            {inputSource === 'upload' ? (
              <Card>
                <CardContent className="p-4">
                  <UploadArea
                    files={uploadedFile ? [uploadedFile] : []}
                    onFilesChange={handleFilesChange}
                    maxFiles={1}
                    acceptedFormats={['image/png', 'image/jpeg', 'image/webp']}
                    disabled={state === 'processing'}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loading size="lg" />
                    </div>
                  ) : images && images.length > 0 ? (
                    <ImageGallery
                      images={images}
                      isLoading={isLoading}
                      isFetching={isFetching}
                      pagination={pagination}
                      onPageChange={setCurrentPage}
                      selectedIds={selectedAssetId ? new Set([selectedAssetId]) : new Set()}
                      onSelectionChange={handleSelectionChange}
                    />
                  ) : (
                    <EmptyState
                      icon={<ImageIcon className="h-12 w-12" />}
                      title={t('gallery.noImages')}
                      description={t('gallery.noImagesDescription')}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Settings Section */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('common.settings')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Mode Toggle: Preset Ratios vs Custom Dimensions */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {t('editor.mode')}
                  </label>
                  <div
                    className="inline-flex w-full rounded-lg border border-neutral-200 dark:border-neutral-700 p-1 bg-neutral-100 dark:bg-neutral-800"
                    role="group"
                    aria-label={t('editor.dimensionsModeAria')}
                  >
                    <button
                      type="button"
                      onClick={() => setCustomMode(false)}
                      disabled={state === 'processing'}
                      aria-pressed={!customMode}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2',
                        !customMode
                          ? 'bg-white dark:bg-neutral-900 text-orange-600 dark:text-orange-400 shadow-sm'
                          : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
                        state === 'processing' && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Grid3X3 className="h-4 w-4" />
                      <span>{t('editor.presets')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomMode(true)}
                      disabled={state === 'processing'}
                      aria-pressed={customMode}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2',
                        customMode
                          ? 'bg-white dark:bg-neutral-900 text-orange-600 dark:text-orange-400 shadow-sm'
                          : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100',
                        state === 'processing' && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Ruler className="h-4 w-4" />
                      <span>{t('editor.custom')}</span>
                    </button>
                  </div>
                </div>

                {/* Ratio Selection - Only visible when NOT in custom mode */}
                {!customMode && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {t('editor.targetRatio')}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {RATIO_KEYS.map((ratio) => (
                      <button
                        key={ratio.value}
                        onClick={() => setTargetRatio(ratio.value)}
                        disabled={state === 'processing'}
                        className={cn(
                          'p-3 rounded-lg border text-center transition-colors',
                          targetRatio === ratio.value
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-900 dark:text-orange-100'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 text-neutral-700 dark:text-neutral-200'
                        )}
                      >
                        <span className="text-xl">{ratio.icon}</span>
                        <p className="text-xs mt-1 font-medium">{ratio.value}</p>
                        <p className={cn(
                          'text-2xs',
                          targetRatio === ratio.value
                            ? 'text-orange-700 dark:text-orange-300'
                            : 'text-neutral-600 dark:text-neutral-400'
                        )}>{t(ratio.labelKey)}</p>
                      </button>
                    ))}
                  </div>
                </div>
                )}

                {/* Custom Dimensions - Only visible when in custom mode */}
                {customMode && (
                <div className="space-y-3">
                  <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {t('editor.targetDimensions')}
                  </label>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Min: 16px, Max: 4096px
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Width Input */}
                    <div className="space-y-1">
                      <label
                        htmlFor="custom-width"
                        className="text-xs font-medium text-neutral-600 dark:text-neutral-400"
                      >
                        {t('editor.widthPx')}
                      </label>
                      <input
                        id="custom-width"
                        type="number"
                        min={16}
                        max={4096}
                        value={customWidth}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomWidth(val === '' ? '' : parseInt(val, 10));
                        }}
                        disabled={state === 'processing'}
                        aria-describedby="width-validation"
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border text-sm',
                          'bg-white dark:bg-neutral-800',
                          'text-neutral-900 dark:text-neutral-100',
                          'border-neutral-200 dark:border-neutral-700',
                          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          typeof customWidth === 'number' && (customWidth < 16 || customWidth > 4096)
                            ? 'border-red-500 dark:border-red-500'
                            : ''
                        )}
                      />
                    </div>
                    {/* Height Input */}
                    <div className="space-y-1">
                      <label
                        htmlFor="custom-height"
                        className="text-xs font-medium text-neutral-600 dark:text-neutral-400"
                      >
                        {t('editor.heightPx')}
                      </label>
                      <input
                        id="custom-height"
                        type="number"
                        min={16}
                        max={4096}
                        value={customHeight}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomHeight(val === '' ? '' : parseInt(val, 10));
                        }}
                        disabled={state === 'processing'}
                        aria-describedby="height-validation"
                        className={cn(
                          'w-full px-3 py-2 rounded-lg border text-sm',
                          'bg-white dark:bg-neutral-800',
                          'text-neutral-900 dark:text-neutral-100',
                          'border-neutral-200 dark:border-neutral-700',
                          'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          typeof customHeight === 'number' && (customHeight < 16 || customHeight > 4096)
                            ? 'border-red-500 dark:border-red-500'
                            : ''
                        )}
                      />
                    </div>
                  </div>
                  {/* Validation Messages */}
                  {(typeof customWidth === 'number' && (customWidth < 16 || customWidth > 4096)) ||
                   (typeof customHeight === 'number' && (customHeight < 16 || customHeight > 4096)) ? (
                    <p className="text-xs text-red-500 dark:text-red-400" role="alert">
                      {t('editor.valueBetween')}
                    </p>
                  ) : null}
                </div>
                )}

                {/* Crop Mode Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {t('editor.cropMode')}
                  </label>
                  <div className="space-y-2">
                    {CROP_MODE_KEYS.map((mode) => (
                      <button
                        key={mode.value}
                        onClick={() => setCropMode(mode.value)}
                        disabled={state === 'processing'}
                        className={cn(
                          'w-full p-3 rounded-lg border text-left transition-colors',
                          cropMode === mode.value
                            ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white dark:bg-neutral-800'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className={cn(
                            'font-medium text-sm',
                            cropMode === mode.value
                              ? 'text-orange-900 dark:text-orange-100'
                              : 'text-neutral-800 dark:text-neutral-100'
                          )}>{t(mode.labelKey)}</span>
                          {cropMode === mode.value && (
                            <Check className="h-4 w-4 text-orange-500" />
                          )}
                        </div>
                        <p className={cn(
                          'text-xs mt-0.5',
                          cropMode === mode.value
                            ? 'text-orange-700 dark:text-orange-300'
                            : 'text-neutral-600 dark:text-neutral-400'
                        )}>{t(mode.descKey)}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Background Extension Toggle */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
                  <div>
                    <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      {t('editor.extendBackground')}
                    </p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {t('editor.aiFillsRemaining')}
                    </p>
                  </div>
                  <Checkbox
                    checked={extendBackground}
                    onChange={(e) => setExtendBackground(e.target.checked)}
                    disabled={state === 'processing'}
                  />
                </div>

                {/* Progress */}
                {state === 'processing' && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('common.processing')}</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-500 transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Process Button */}
                <Button
                  className="w-full"
                  onClick={handleProcess}
                  disabled={!canProcess || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      {t('common.processing')}
                    </>
                  ) : (
                    <>
                      <Maximize2 className="h-4 w-4 mr-2" />
                      {hasInput ? t('editor.changeRatioButton') : t('editor.selectImageButton')}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export default AspectRatioMode;
